/**
 * Pure follow state machine for read-along auto-scroll, shared by all four
 * surfaces (web/mobile × text/PDF). `followReduce(state, event, config)` is
 * the ENTIRE behavior spec — no platform code makes scroll decisions.
 *
 * Design: docs/projects/active/read-along-follow-unification.md
 *
 * Coordinate contract: a resolved `ScrollTarget` of kind `offset` is the
 * REST-PLACEMENT scrollY — "scroll the viewport here and the active word sits
 * at its designed rest position" (the resolvers bake in top margin/padding and
 * clamping; see doc-pack/text-layout.ts and doc-pack/pdf-layout.ts). The
 * comfort band is therefore expressed as tolerated DRIFT of the viewport from
 * that rest placement, in fractions of viewport height:
 *
 *   drift d = target.y - viewport.scrollY
 *   in band ⇔ -band.up * height ≤ d ≤ band.down * height
 *
 * `{up: 0, down: 0}` tolerates no drift — every word change scrolls (the
 * teleprompter behavior the mobile readers shipped with). A comfort band like
 * `{up: 0.1, down: 0.4}` lets the word walk down 40% of the viewport before
 * one calm scroll restores the rest placement.
 *
 * Invariant the controller depends on: `scrolling: 'animating'` means a
 * `scroll` command is in flight and settle detection is armed for it. Any
 * transition that enters or stays in `animating` must therefore emit a scroll
 * (or leave one already-armed alone), and any `cancel-scroll` must either
 * return to `idle` or be followed by a replacement scroll in the same
 * transition. follow-controller.ts gates its sampler on this state; breaking
 * it degrades follow to the controller's bounded failsafe. Guarded
 * exhaustively by read-along-follow-machine.test.ts.
 */

export type ScrollTarget =
  | { readonly kind: 'offset'; readonly y: number }
  /** Coarse fallback when the word's row isn't measured/mounted yet: scroll
   * to the page row; the next anchor tick refines to the word offset. */
  | { readonly kind: 'page'; readonly index: number };

export type FollowViewport = {
  readonly scrollY: number;
  readonly height: number;
};

export type FollowBand = {
  /** Tolerated upward drift (word above rest), fraction of viewport height. */
  readonly up: number;
  /** Tolerated downward drift (word below rest), fraction of viewport height. */
  readonly down: number;
};

export type FollowConfig = {
  readonly band: FollowBand;
  /** Sub-pixel/rounding slack so restoring rest never oscillates. */
  readonly epsilonPx?: number;
};

export const TELEPROMPTER_BAND: FollowBand = { up: 0, down: 0 };

export type FollowEvent =
  | {
      readonly type: 'anchor';
      /** Rest-placement target for the current anchor; null = unresolvable. */
      readonly target: ScrollTarget | null;
      readonly viewport: FollowViewport;
      /** Whether the active word is currently visible in the viewport. */
      readonly activeVisible: boolean;
    }
  | { readonly type: 'user-scroll-start' }
  | {
      readonly type: 'user-scroll-end';
      readonly viewport: FollowViewport;
      readonly activeVisible: boolean;
    }
  /** Pill taps scroll to the current anchor NOW (default), even when the
   * auto-scroll preference is off; later anchors still respect the preference.
   * Word taps pass `immediateScroll: false` — their seek moves the anchor. */
  | { readonly type: 'engage'; readonly immediateScroll?: boolean }
  | { readonly type: 'pref'; readonly enabled: boolean }
  | {
      readonly type: 'scroll-settled';
      readonly viewport: FollowViewport;
      readonly activeVisible: boolean;
    }
  /** Resize, font change, zoom: previous targets/memos are meaningless. */
  | { readonly type: 'layout-invalidated' };

export type FollowCommand =
  | {
      readonly type: 'scroll';
      readonly target: ScrollTarget;
      readonly animated: boolean;
    }
  /** Stop an in-flight programmatic scroll (user grabbed the viewport). */
  | { readonly type: 'cancel-scroll' }
  | { readonly type: 'pill'; readonly visible: boolean };

export type FollowState = {
  readonly prefEnabled: boolean;
  /** False after a user scroll until an explicit engage. */
  readonly engaged: boolean;
  /** `animating` ⇒ a scroll is in flight with settle detection armed for it
   * (see the invariant in the module header). */
  readonly scrolling: 'idle' | 'animating';
  /** Latest resolved target (kept even while disengaged, for engage/pill). */
  readonly target: ScrollTarget | null;
  /** An anchor arrived while animating; re-evaluate on scroll-settled. */
  readonly pendingAnchor: boolean;
  /** Last target we emitted a scroll for (dedupes coarse page scrolls, and
   * marks a page jump as awaiting its precise refinement). */
  readonly lastScrolled: ScrollTarget | null;
  readonly pillVisible: boolean;
};

export function initialFollowState(prefEnabled: boolean): FollowState {
  return {
    prefEnabled,
    engaged: true,
    scrolling: 'idle',
    target: null,
    pendingAnchor: false,
    lastScrolled: null,
    pillVisible: false,
  };
}

export function scrollTargetsEqual(
  a: ScrollTarget | null,
  b: ScrollTarget | null,
): boolean {
  if (a === null || b === null) return a === b;
  if (a.kind === 'offset' && b.kind === 'offset') return a.y === b.y;
  if (a.kind === 'page' && b.kind === 'page') return a.index === b.index;
  return false;
}

const DEFAULT_EPSILON_PX = 1;

/** The word's row has now been measured after a coarse page jump, so the
 * offset we just resolved is the FIRST precise placement we have had. Page
 * jumps land the row anywhere on screen, which is often already "in band" by
 * accident — so this refinement is exempt from the band check (rule 6),
 * otherwise the reader stops one imprecise jump short of rest placement. */
const isPrecisePageRefinement = (
  lastScrolled: ScrollTarget | null,
  target: ScrollTarget,
) => lastScrolled?.kind === 'page' && target.kind === 'offset';

/** Rule 5: playback-driven scrolls fire only when the viewport has drifted
 * outside the band around the target's rest placement. */
export function isDriftInsideBand(
  target: ScrollTarget,
  viewport: FollowViewport,
  config: FollowConfig,
): boolean {
  // Coarse page targets carry no y — never "in band" (rule 6: scroll, refine).
  if (target.kind !== 'offset') return false;
  const epsilon = config.epsilonPx ?? DEFAULT_EPSILON_PX;
  const drift = target.y - viewport.scrollY;
  return (
    drift >= -(config.band.up * viewport.height) - epsilon &&
    drift <= config.band.down * viewport.height + epsilon
  );
}

/** Offset scrolls glide; coarse page jumps land instantly (rule 6). */
const scrollCommand = (target: ScrollTarget): FollowCommand => ({
  type: 'scroll',
  target,
  animated: target.kind === 'offset',
});

/** Rule 7 (+8): pill ⇔ pref on ∧ disengaged ∧ resolved ∧ word off-viewport. */
const pillShouldShow = (state: FollowState, activeVisible: boolean): boolean =>
  state.prefEnabled &&
  !state.engaged &&
  state.target !== null &&
  !activeVisible;

const withPill = (
  state: FollowState,
  activeVisible: boolean,
  commands: FollowCommand[],
): FollowState => {
  const visible = pillShouldShow(state, activeVisible);
  if (visible === state.pillVisible) return state;
  commands.push({ type: 'pill', visible });
  return { ...state, pillVisible: visible };
};

export function followReduce(
  state: FollowState,
  event: FollowEvent,
  config: FollowConfig,
): [FollowState, FollowCommand[]] {
  const commands: FollowCommand[] = [];

  switch (event.type) {
    case 'pref': {
      if (event.enabled === state.prefEnabled) return [state, commands];
      if (event.enabled)
        return [
          withPill(
            { ...state, prefEnabled: true, engaged: true },
            true,
            commands,
          ),
          commands,
        ];
      let next: FollowState = { ...state, prefEnabled: false };
      // Rule 8: pref off → no commands, no pill (kill anything in flight).
      if (next.scrolling === 'animating')
        commands.push({ type: 'cancel-scroll' });
      next = { ...next, scrolling: 'idle', pendingAnchor: false };
      if (next.pillVisible) {
        commands.push({ type: 'pill', visible: false });
        next = { ...next, pillVisible: false };
      }
      return [next, commands];
    }

    case 'user-scroll-start': {
      // Rule 2: disengage immediately; cancel any in-flight programmatic
      // scroll. Adapters guarantee this event only fires for USER gestures.
      let next: FollowState = { ...state, engaged: false };
      if (state.scrolling === 'animating') {
        commands.push({ type: 'cancel-scroll' });
        next = { ...next, scrolling: 'idle', pendingAnchor: false };
      }
      return [next, commands];
    }

    case 'user-scroll-end': {
      return [withPill(state, event.activeVisible, commands), commands];
    }

    case 'engage': {
      // Rule 3: engage scrolls to the current anchor immediately (band is a
      // damper for playback drift, not for explicit user intent).
      let next: FollowState = { ...state, engaged: true };
      next = withPill(next, true, commands);
      if (event.immediateScroll === false) return [next, commands];
      // The cancel below is always followed by a replacement scroll or a
      // return to idle — see the invariant in the module header.
      if (next.scrolling === 'animating') {
        commands.push({ type: 'cancel-scroll' });
        next = { ...next, scrolling: 'idle', pendingAnchor: false };
      }
      if (next.target !== null) {
        commands.push(scrollCommand(next.target));
        next = {
          ...next,
          scrolling: 'animating',
          pendingAnchor: false,
          lastScrolled: next.target,
        };
      }
      return [next, commands];
    }

    case 'anchor': {
      let next: FollowState = { ...state, target: event.target };
      next = withPill(next, event.activeVisible, commands);
      // Rule 1: scroll only when pref on and engaged.
      if (!next.prefEnabled || !next.engaged || next.target === null)
        return [next, commands];
      // Rule 4: coalesce while animating; latest target is already stored.
      if (next.scrolling === 'animating')
        return [{ ...next, pendingAnchor: true }, commands];
      // Rule 5, with the rule-6 refinement exemption.
      if (
        !isPrecisePageRefinement(next.lastScrolled, next.target) &&
        isDriftInsideBand(next.target, event.viewport, config)
      )
        return [next, commands];
      // Rule 6: don't re-emit the same coarse page jump every tick while the
      // row stays unmounted.
      if (
        next.target.kind === 'page' &&
        scrollTargetsEqual(next.lastScrolled, next.target)
      )
        return [next, commands];
      commands.push(scrollCommand(next.target));
      return [
        { ...next, scrolling: 'animating', lastScrolled: next.target },
        commands,
      ];
    }

    case 'scroll-settled': {
      let next: FollowState = { ...state, scrolling: 'idle' };
      const rerun = next.pendingAnchor;
      next = { ...next, pendingAnchor: false };
      next = withPill(next, event.activeVisible, commands);
      // Rule 4: at most one animated scroll in flight — the coalesced anchor
      // is evaluated now, against the settled viewport.
      if (
        rerun &&
        next.prefEnabled &&
        next.engaged &&
        next.target !== null &&
        (isPrecisePageRefinement(next.lastScrolled, next.target) ||
          !isDriftInsideBand(next.target, event.viewport, config))
      ) {
        commands.push(scrollCommand(next.target));
        next = {
          ...next,
          scrolling: 'animating',
          lastScrolled: next.target,
        };
      }
      return [next, commands];
    }

    case 'layout-invalidated': {
      // Stale geometry must not suppress the next scroll (page-jump dedupe)
      // — and any in-flight animation is aiming at a stale target.
      let next: FollowState = { ...state, lastScrolled: null, target: null };
      if (state.scrolling === 'animating') {
        commands.push({ type: 'cancel-scroll' });
        next = { ...next, scrolling: 'idle', pendingAnchor: false };
      }
      // No resolved target ⇒ no pill (rule 7) until the next anchor tick.
      if (next.pillVisible) {
        commands.push({ type: 'pill', visible: false });
        next = { ...next, pillVisible: false };
      }
      return [next, commands];
    }
    default: {
      return event satisfies never;
    }
  }
}
